import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Check, CheckCircle, AlertTriangle, Info, CreditCard, Shield, Settings, Trash2 } from 'lucide-react';
import { notificationsAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState({ unread_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, transaction, security, etc.
  const dropdownRef = useRef(null);
  const notification = useNotification();

  // Load notifications
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const params = {
        per_page: 50,
        ...(filter === 'unread' && { unread_only: true }),
        ...(filter === 'read' && { read_only: true }),
        ...(typeFilter !== 'all' && { type: typeFilter })
      };

      const [notificationsResponse, summaryResponse] = await Promise.all([
        notificationsAPI.getNotifications(params),
        notificationsAPI.summary()
      ]);

      setNotifications(notificationsResponse.data.notifications);
      setSummary(summaryResponse.data);
    } catch (error) {
      notification.showError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setSummary(prev => ({ ...prev, unread_count: prev.unread_count - 1 }));
    } catch (error) {
      notification.showError('Failed to mark notification as read');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const params = typeFilter !== 'all' ? { type: typeFilter } : {};
      await notificationsAPI.markAllAsRead(params);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setSummary(prev => ({ ...prev, unread_count: 0 }));
      notification.showSuccess('All notifications marked as read');
    } catch (error) {
      notification.showError('Failed to mark all notifications as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setSummary(prev => ({ 
        ...prev, 
        total_count: prev.total_count - 1,
        unread_count: prev.unread_count - (notifications.find(n => n.id === notificationId && !n.is_read) ? 1 : 0)
      }));
      notification.showSuccess('Notification deleted');
    } catch (error) {
      notification.showError('Failed to delete notification');
    }
  };

  // Get notification icon
  const getNotificationIcon = (type, priority) => {
    const iconProps = {
      className: `w-5 h-5 ${
        priority === 'high' ? 'text-red-600' :
        priority === 'medium' ? 'text-yellow-600' : 'text-blue-600'
      }`
    };

    switch (type) {
      case 'security':
        return <Shield {...iconProps} />;
      case 'card':
        return <CreditCard {...iconProps} />;
      case 'transaction':
      case 'transfer':
      case 'zelle':
      case 'bill_payment':
        return <CheckCircle {...iconProps} />;
      case 'system':
        return <Settings {...iconProps} />;
      default:
        return <Info {...iconProps} />;
    }
  };

  // Get notification background color
  const getNotificationBg = (type, isRead, priority) => {
    const opacity = isRead ? 'bg-opacity-30' : 'bg-opacity-60';
    
    if (priority === 'high') return `bg-red-50 border-red-200 ${opacity}`;
    if (priority === 'medium') return `bg-yellow-50 border-yellow-200 ${opacity}`;
    return `bg-blue-50 border-blue-200 ${opacity}`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load notifications when component mounts or filters change
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, filter, typeFilter]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="w-6 h-6" />
        {summary.unread_count > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {summary.unread_count > 99 ? '99+' : summary.unread_count}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-2xl border border-gray-200 z-50
                       fixed top-16 left-2 right-2 mt-2
                       sm:absolute sm:right-0 sm:left-auto sm:top-auto sm:w-96 sm:max-w-sm"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <div className="flex items-center space-x-2">
                  {summary.unread_count > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                <span>{summary.total_count} total</span>
                <span>{summary.unread_count} unread</span>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-3">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-2 py-1"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-2 py-1"
                >
                  <option value="all">All Types</option>
                  <option value="transaction">Transactions</option>
                  <option value="security">Security</option>
                  <option value="card">Cards</option>
                  <option value="crypto">Crypto</option>
                  <option value="support">Support</option>
                </select>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No notifications found</p>
                </div>
              ) : (
                <div className="p-2">
                  {notifications.map((notif) => (
                    <motion.div
                      key={notif.id}
                      layout
                      className={`p-3 rounded-lg border mb-2 ${getNotificationBg(notif.type, notif.is_read, notif.priority)} ${
                        !notif.is_read ? 'ring-1 ring-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notif.type, notif.priority)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 pr-2">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                {notif.title}
                              </p>
                              <p className="text-sm text-gray-700 mb-2 break-words">
                                {notif.message}
                              </p>
                              <p className="text-xs text-gray-500">
                                {notif.time_ago}
                              </p>
                            </div>
                            <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-1">
                              {!notif.is_read && (
                                <button
                                  onClick={() => markAsRead(notif.id)}
                                  className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                                  title="Mark as read"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notif.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 text-center">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    // Navigate to full notifications page if needed
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View all notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter; 